import { useState, useCallback } from 'react'
import { useLoadedManta } from '@happy-ops-org/manta-react'
import {
  Box,
  Typography,
  Button,
  Paper,
  Alert,
  Chip,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Divider,
} from '@mui/material'
import UploadFileIcon from '@mui/icons-material/UploadFile'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import ErrorIcon from '@mui/icons-material/Error'
import { isRight } from 'fp-ts/Either'
import { PathReporter } from 'io-ts/PathReporter'
import { importFileCodec } from './codecs.js'
import { collectionMetaDef } from './manta.js'
import type { CollectionAnnotation } from './types.js'

type ValidationState =
  | { status: 'idle' }
  | { status: 'error'; message: string }
  | { status: 'invalid'; errors: string[] }
  | {
      status: 'valid'
      data: Record<string, CollectionAnnotation>
      filename: string
    }
  | { status: 'importing' }
  | { status: 'done'; count: number }

const EXAMPLE_JSON = JSON.stringify(
  {
    orders: {
      description: 'Produktionsaufträge. Status: open → running → done.',
      readOnly: false,
      tags: ['cooperative'],
      fields: {
        status: {
          description: 'Aktueller Bearbeitungsstatus',
          examples: ['open', 'running', 'done'],
        },
        machine_id: {
          description: 'Zugewiesene Maschine',
          relatesTo: 'machines.id',
        },
      },
    },
    machines: {
      description: 'Maschinenstammdaten',
      readOnly: true,
      tags: ['machine-data'],
    },
  },
  null,
  2,
)

export const ImportPage = () => {
  const manta = useLoadedManta()
  const [validation, setValidation] = useState<ValidationState>({
    status: 'idle',
  })
  const [dragging, setDragging] = useState(false)

  const processFile = async (file: File) => {
    setValidation({ status: 'idle' })
    try {
      const text = await file.text()
      const json = JSON.parse(text)
      const result = importFileCodec.decode(json)

      if (isRight(result)) {
        setValidation({
          status: 'valid',
          data: result.right,
          filename: file.name,
        })
      } else {
        setValidation({
          status: 'invalid',
          errors: PathReporter.report(result),
        })
      }
    } catch (err) {
      setValidation({
        status: 'error',
        message: `Ungültiges JSON: ${String(err)}`,
      })
    }
  }

  const handleFileInput = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (file) processFile(file)
    e.target.value = ''
  }

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault()
    setDragging(false)
    const file = e.dataTransfer.files[0]
    if (file?.name.endsWith('.json')) processFile(file)
  }, [])

  const handleImport = async () => {
    if (validation.status !== 'valid') return
    const { data } = validation
    setValidation({ status: 'importing' })
    await Promise.all(
      Object.entries(data).map(([lid, annotation]) =>
        manta.updateOrCreate(collectionMetaDef, lid, annotation),
      ),
    )
    setValidation({ status: 'done', count: Object.keys(data).length })
  }

  return (
    <Box p={4} maxWidth={800} mx="auto">
      <Typography variant="h5" fontWeight={700} mb={1}>
        Import
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={4}>
        Lade eine JSON-Datei hoch um mehrere Collection-Definitionen auf einmal
        zu schreiben. Die Daten werden vor dem Import gegen das Schema
        validiert.
      </Typography>

      {/* Drop Zone */}
      <Paper
        variant="outlined"
        onDragOver={(e) => {
          e.preventDefault()
          setDragging(true)
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={handleDrop}
        sx={{
          p: 5,
          mb: 4,
          textAlign: 'center',
          borderStyle: 'dashed',
          borderWidth: 2,
          borderColor: dragging ? 'primary.main' : 'divider',
          bgcolor: dragging ? 'primary.50' : 'grey.50',
          transition: 'all 0.15s',
          cursor: 'pointer',
        }}
        onClick={() => document.getElementById('import-file-input')?.click()}
      >
        <UploadFileIcon
          sx={{
            fontSize: 48,
            color: dragging ? 'primary.main' : 'text.disabled',
            mb: 1,
          }}
        />
        <Typography
          fontWeight={600}
          color={dragging ? 'primary.main' : 'text.secondary'}
        >
          JSON-Datei hier ablegen oder klicken zum Auswählen
        </Typography>
        <Typography variant="caption" color="text.disabled">
          Nur .json Dateien
        </Typography>
        <input
          id="import-file-input"
          type="file"
          accept=".json"
          style={{ display: 'none' }}
          onChange={handleFileInput}
        />
      </Paper>

      {/* Validation Result */}
      {validation.status === 'error' && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {validation.message}
        </Alert>
      )}

      {validation.status === 'invalid' && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<ErrorIcon />}>
          <Typography fontWeight={600} mb={1}>
            Schema-Fehler
          </Typography>
          <Stack spacing={0.5}>
            {validation.errors.map((e, i) => (
              <Typography
                key={i}
                variant="body2"
                fontFamily="monospace"
                fontSize={12}
              >
                {e}
              </Typography>
            ))}
          </Stack>
        </Alert>
      )}

      {validation.status === 'valid' && (
        <Alert severity="success" icon={<CheckCircleIcon />} sx={{ mb: 3 }}>
          <Box
            display="flex"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box>
              <Typography fontWeight={600}>
                {Object.keys(validation.data).length} Collections validiert —{' '}
                {validation.filename}
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={0.5} mt={1}>
                {Object.keys(validation.data).map((name) => (
                  <Chip key={name} label={name} size="small" />
                ))}
              </Box>
            </Box>
            <Button
              variant="contained"
              onClick={handleImport}
              sx={{ ml: 2, flexShrink: 0 }}
            >
              Importieren
            </Button>
          </Box>
        </Alert>
      )}

      {validation.status === 'importing' && (
        <Alert severity="info" sx={{ mb: 3 }}>
          Import läuft…
        </Alert>
      )}

      {validation.status === 'done' && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {validation.count} Collections erfolgreich importiert.
        </Alert>
      )}

      <Divider sx={{ mb: 4 }} />

      {/* Schema Documentation */}
      <Typography variant="h6" fontWeight={700} mb={2}>
        Erwartetes Format
      </Typography>
      <Typography variant="body2" color="text.secondary" mb={2}>
        Die Datei muss ein JSON-Objekt sein, dessen Keys die Collection-Namen (=
        tableName) sind.
      </Typography>

      <Table size="small" sx={{ mb: 4 }}>
        <TableHead>
          <TableRow>
            <TableCell>
              <strong>Feld</strong>
            </TableCell>
            <TableCell>
              <strong>Typ</strong>
            </TableCell>
            <TableCell>
              <strong>Pflicht</strong>
            </TableCell>
            <TableCell>
              <strong>Beschreibung</strong>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {[
            [
              'description',
              'string',
              '✓',
              'Beschreibung der Collection für das LLM',
            ],
            ['readOnly', 'boolean', '–', 'Blockiert Write-Tools wenn true'],
            [
              'tags',
              'string[]',
              '–',
              'Gruppierungs-Tags, z.B. ["machine-data"]',
            ],
            ['fields', 'object', '–', 'Feld-Annotationen (siehe unten)'],
            [
              'fields.[name].description',
              'string',
              '✓',
              'Bedeutung des Feldes',
            ],
            ['fields.[name].examples', 'unknown[]', '–', 'Beispielwerte'],
            [
              'fields.[name].relatesTo',
              'string',
              '–',
              'Referenz, z.B. "machines.id"',
            ],
          ].map(([field, type, required, desc]) => (
            <TableRow key={field}>
              <TableCell>
                <code>{field}</code>
              </TableCell>
              <TableCell>
                <code>{type}</code>
              </TableCell>
              <TableCell>{required}</TableCell>
              <TableCell>{desc}</TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      <Typography variant="subtitle2" fontWeight={600} mb={1}>
        Beispiel
      </Typography>
      <Paper
        variant="outlined"
        sx={{ p: 2, bgcolor: 'grey.50', overflow: 'auto' }}
      >
        <pre style={{ margin: 0, fontSize: 12, fontFamily: 'monospace' }}>
          {EXAMPLE_JSON}
        </pre>
      </Paper>
    </Box>
  )
}
